import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useRazorpayCheckout from "../hooks/useRazorpayCheckout";
import { useUser } from "../context/UserContext";
import { toast } from "../lib/toast";
import { placeWaivedOpsBooking } from "../api/opsBookingApi";

const fmtINR = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    Number.isFinite(+amount) ? +amount : 0
  );

const n = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const qtyOf = (item) => Math.max(1, Number(item?.quantity ?? 1));
const ceilMoney = (value) => Math.ceil(Math.max(0, n(value)));
const bookingAmountOf = (item) =>
  ceilMoney(n(item?.commission) + n(item?.apiCharges) + n(item?.gst)) * qtyOf(item);

function cleanPhone(phone = "") {
  return String(phone).replace(/\D/g, "").slice(-10);
}

export default function CheckoutBar({
  cartItems = [],
  totalBookingAmount: totalBookingAmountProp,
  totalFare = 0,
  totalPendingCash = 0,
  totalOriginalBookingAmount = 0,
  discountPercent = 0,
}) {
  const { user } = useUser();
  const navigate = useNavigate();
  const { openCheckout } = useRazorpayCheckout();
  const [loading, setLoading] = useState(false);
  const clickedRef = useRef(false);

  const totalBookingAmount = useMemo(
    () =>
      typeof totalBookingAmountProp === "number"
        ? totalBookingAmountProp
        : Array.isArray(cartItems)
        ? cartItems.reduce((acc, item) => acc + bookingAmountOf(item), 0)
        : 0,
    [cartItems, totalBookingAmountProp]
  );

  const userId =
    typeof user?.id === "number"
      ? user.id
      : typeof user?.userId === "number"
      ? user.userId
      : null;

  const phone10 = user?.phoneNumber ? cleanPhone(user.phoneNumber) : "";
  const disabled =
    (!userId && !phone10 && !user?.email) ||
    !cartItems.length ||
    totalFare <= 0 ||
    loading;
  const fullyWaived = totalBookingAmount <= 0;
  const hasOffer =
    Math.max(0, Number(totalOriginalBookingAmount || 0) - Number(totalBookingAmount || 0)) > 0.009;

  const onPlaceBooking = async () => {
    if (clickedRef.current) return;
    clickedRef.current = true;

    try {
      setLoading(true);

      if (totalBookingAmount <= 0) {
        const result = await placeWaivedOpsBooking({ userId });
        if (!result?.success) {
          toast.error("Unable to place booking", result?.message || "Please try again.");
          setLoading(false);
          clickedRef.current = false;
          return;
        }
        toast.success(
          "Booking placed successfully",
          "No payment was needed now. The ride stays pay-after-ride."
        );
        navigate("/my-bookings", { replace: true });
        setLoading(false);
        clickedRef.current = false;
        return;
      }

      await openCheckout({
        amount: totalBookingAmount,
        flow: "ops",
        user: {
          id: userId,
          name: user?.name || user?.userName || "Customer",
          email: user?.email || "",
          phone: phone10 || user?.phone || "",
        },
        notes: { source: "wayroot-cart-web", flow: "ops" },
        onVerified: async (result) => {
          if (!result?.success) {
            toast.error("Payment verification failed", result?.message || "Please try again.");
            setLoading(false);
            clickedRef.current = false;
            return;
          }

          toast.success(
            "Booking placed successfully",
            "Your trip requests are now being processed."
          );
          navigate("/my-bookings", { replace: true });
          setLoading(false);
          clickedRef.current = false;
        },
        onClose: () => {
          setLoading(false);
          clickedRef.current = false;
        },
      });
    } catch (error) {
      console.error(error);
      toast.error("Unable to start payment", error?.message || "Please try again.");
      setLoading(false);
      clickedRef.current = false;
    }
  };

  return (
    <div
      className="sticky bottom-0 z-30 mt-6 -mx-4 border-t border-slate-200 bg-white shadow-[0_-10px_24px_rgba(15,23,42,0.08)] sm:mx-0 sm:mt-8 sm:rounded-2xl sm:border sm:shadow-xl"
      role="region"
      aria-label="Checkout summary"
    >
      <div className="px-4 py-3 sm:px-6 sm:py-5 lg:flex lg:items-center lg:gap-5">
        <div
          className={`rounded-2xl border px-4 py-3 lg:flex-1 lg:px-5 lg:py-2.5 ${
            fullyWaived || hasOffer
              ? "border-amber-200 bg-gradient-to-r from-amber-50 via-rose-50 to-white"
              : "border-slate-200 bg-slate-50"
          }`}
        >
          <div className="grid grid-cols-2 gap-3 lg:gap-4">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                Booking amount
              </div>
              <div className="mt-1 text-[22px] font-extrabold tracking-tight text-slate-900 tabular-nums sm:text-[24px] lg:mt-0.5 lg:text-[18px]">
                {fullyWaived ? "\u20B90" : fmtINR(totalBookingAmount)}
              </div>
            </div>
            <div className="min-w-0 text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                Pay after ride
              </div>
              <div className="mt-1 text-[22px] font-extrabold tracking-tight text-slate-900 tabular-nums sm:text-[24px] lg:mt-0.5 lg:text-[18px]">
                {fmtINR(totalPendingCash)}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={onPlaceBooking}
          aria-busy={loading ? "true" : "false"}
          className="mt-3 inline-flex min-h-[52px] w-full items-center justify-center rounded-2xl px-5 py-3 text-[15px] font-semibold text-white transition hover:translate-y-[-1px] active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 sm:min-h-[58px] sm:px-8 sm:py-4 sm:text-base sm:shadow-[0_16px_32px_rgba(15,23,42,0.18)] lg:mt-0 lg:min-h-[74px] lg:w-[300px] lg:flex-none lg:px-8 lg:py-4 lg:text-[18px]"
          style={{ background: "#0B0E13" }}
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  opacity="0.25"
                />
                <path
                  d="M22 12a10 10 0 0 1-10 10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
              </svg>
              Processing...
            </span>
          ) : (
            "Place Booking"
          )}
        </button>
      </div>
    </div>
  );
}
